import { useEffect, useState } from "react";
import type { TicketAttachment } from "@/mocks/ticketData";
import { fetchLiveTicketAttachment } from "@/services/zammadService";

interface TicketAttachmentsProps {
  attachments: TicketAttachment[];
  customerIdentity?: string;
}

function formatFileSize(size: number): string {
  if (!size) return "Tamaño no disponible";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentItem({
  attachment,
  customerIdentity,
}: {
  attachment: TicketAttachment;
  customerIdentity?: string;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isImage = attachment.mimeType.toLowerCase().startsWith("image/");

  useEffect(() => {
    if (!isImage || !customerIdentity) return;

    let active = true;
    let objectUrl: string | null = null;
    setPreviewError(false);

    fetchLiveTicketAttachment(attachment, customerIdentity)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active) setPreviewError(true);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, customerIdentity, isImage]);

  const downloadAttachment = async () => {
    if (!customerIdentity || downloading) return;

    setDownloading(true);
    try {
      const blob = await fetchLiveTicketAttachment(attachment, customerIdentity);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (error) {
      console.error("Failed to download ticket attachment:", error);
      alert(`No se pudo descargar “${attachment.filename}”.`);
    } finally {
      setDownloading(false);
    }
  };

  if (isImage) {
    return (
      <li className="w-28">
        <button
          type="button"
          onClick={downloadAttachment}
          disabled={!customerIdentity || downloading}
          className="group w-full text-left disabled:cursor-not-allowed"
          title={`Descargar ${attachment.filename}`}
        >
          <span className="flex h-24 w-28 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={attachment.filename}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            ) : (
              <i
                className={`${previewError ? "ri-image-off-line" : "ri-image-line"} text-2xl text-foreground-300`}
                aria-hidden="true"
              />
            )}
          </span>
          <span className="mt-1.5 block truncate text-[11px] font-medium text-foreground-700">
            {attachment.filename}
          </span>
          <span className="block text-[10px] text-foreground-400">
            {downloading ? "Descargando..." : formatFileSize(attachment.size)}
          </span>
        </button>
      </li>
    );
  }

  return (
    <li className="min-w-0">
      <button
        type="button"
        onClick={downloadAttachment}
        disabled={!customerIdentity || downloading}
        className="flex max-w-full items-center gap-2.5 rounded-lg border border-background-200 bg-background-100/70 px-3 py-2 text-left transition-colors hover:border-primary-200 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
        title={`Descargar ${attachment.filename}`}
      >
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-500">
          <i className="ri-file-download-line" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-xs font-medium text-foreground-700">
            {attachment.filename}
          </span>
          <span className="block text-[10px] text-foreground-400">
            {downloading ? "Descargando..." : formatFileSize(attachment.size)}
          </span>
        </span>
      </button>
    </li>
  );
}

export default function TicketAttachments({
  attachments,
  customerIdentity,
}: TicketAttachmentsProps) {
  if (attachments.length === 0) return null;

  return (
    <div className="px-6 py-4 border-b border-background-200/70">
      <h4 className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
        <i className="ri-attachment-2" aria-hidden="true" />
        Archivos adjuntos ({attachments.length})
      </h4>
      <ul className="flex flex-wrap gap-3">
        {attachments.map((attachment) => (
          <AttachmentItem
            key={`${attachment.articleId}-${attachment.id}`}
            attachment={attachment}
            customerIdentity={customerIdentity}
          />
        ))}
      </ul>
    </div>
  );
}
